import { NameAssertion } from "@/entities/form-metadata";
import { useTrackSearchFormInteraction } from "@/shared/lib/ube";

const NAME_OBJECT_NAME: Partial<Record<NameAssertion, string>> = {
    [NameAssertion.GivenName]: "given_name",
    [NameAssertion.Surname]: "surname",
    [NameAssertion.SecondSurname]: "second_surname",
};

/**
 * UBE focus tracking for the top-level Given Name/Surname/Second Surname fields. Each is a
 * single, non-repeating instance per form, so it's identified by a constant `objectName` rather
 * than compositional relation/field identity — see `docs/engineering/ube.md`.
 *
 * Returns a lookup function rather than tracking a single assertion directly, so it can be
 * called once per assertion inside a render loop without violating the Rules of Hooks. The
 * lookup's `onFocus` is `undefined` for assertions with no tracked `objectName`, so callers can
 * spread the result onto an input without conditionally including the prop themselves.
 */
export function useNameFieldFocusTracking(): (assertion: NameAssertion) => { onFocus: (() => void) | undefined } {
    const track = useTrackSearchFormInteraction();

    return (assertion) => {
        const objectName = NAME_OBJECT_NAME[assertion];
        return {
            onFocus:
                objectName !== undefined ? () => track({ action: "focus", objectName }, { dedup: true }) : undefined,
        };
    };
}
